import CallSplitIcon from "@mui/icons-material/CallSplit";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useAIAgentThread } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./AgentThreadFeedback.module.css";

interface ForkAgentThreadButtonProps {
  threadId: string;
  /** Org the source thread belongs to. Passed through as ``X-Roboto-Org-Id``
   *  so the backend's ``REQUIRES_CALLER_ORG`` check can pick an org for
   *  callers who belong to multiple orgs (admins). */
  agentThreadOrgId: string;
  /** Sequence number of the message (inclusive) to fork at. Typically the
   *  sequence number of the assistant message the button is rendered next to. */
  messageSequenceNum: number;
  /** Disable while an upstream caller knows the message isn't a valid fork point. */
  disabled?: boolean;
}

/**
 * Trigger a fork of this thread at the given message.
 *
 * The fork endpoint is available to the thread's owner (forking their own
 * thread) and to Roboto admins (forking anyone's). The new thread is owned by
 * the caller and navigated to on success.
 */
export const ForkAgentThreadButton: React.FC<ForkAgentThreadButtonProps> = ({
  threadId,
  agentThreadOrgId,
  messageSequenceNum,
  disabled,
}) => {
  const { agentThreadService } = useDomainServices();
  const { handleHistoryThreadClicked } = useAIAgentThread();
  const queryClient = useQueryClient();

  const fork = useMutation<AgentThreadRecord, Error, void>({
    mutationFn: () =>
      agentThreadService.forkAgentThread(threadId, messageSequenceNum, {
        orgId: agentThreadOrgId,
      }),
    onSuccess: async (forked) => {
      // Invalidate every cached thread-list/history query so the new fork
      // appears alongside the user's other threads. Without this the sidebar
      // history pane shows the new thread only on the next manual refresh.
      await queryClient.invalidateQueries({
        queryKey: ["agentThreadsForUser"],
      });
      // Swap the sidebar to the new thread in place; no URL routing because
      // the thread sidebar is an overlay, not a page.
      handleHistoryThreadClicked(forked.thread_id);
    },
    onError: (error) => {
      ErrorMonitoringService.captureError(error, {
        context: { threadId, messageSequenceNum },
      });
    },
  });

  const isDisabled = disabled || fork.isPending;
  // Distinguish "the parent told me to wait for the response to finish"
  // from "I'm currently forking" — the previous tooltip claimed the former
  // for both cases, which was visibly wrong while the spinner was up.
  let tooltip: string;
  if (fork.isPending) {
    tooltip = "Forking…";
  } else if (disabled) {
    tooltip = "Wait for the response to finish";
  } else {
    tooltip = "Fork this agentThread from here";
  }

  return (
    <RobotoTooltip title={tooltip}>
      <span>
        <RobotoIconButton
          eventName="AgentThreadForkClicked"
          eventProperties={{ threadId, messageSequenceNum }}
          onClick={() => fork.mutate()}
          className={styles.iconButton}
          disabled={isDisabled}
        >
          <CallSplitIcon className={styles.actionIcon} />
        </RobotoIconButton>
      </span>
    </RobotoTooltip>
  );
};
